/**
 * StrategyProgressShareModal - Modal for sharing strategy progress achievements
 *
 * Features:
 * - Preview of share card with strategy progress
 * - Multiple share options (Copy link, Instagram, WhatsApp, Download)
 * - Canvas-based image generation with deaveris.com branding
 */
import { useState, useEffect } from 'react'
import { X, Link2, Download, Check, Share2, Instagram, MessageCircle, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import strategyLevelService from '../services/strategyLevelService'
import { createLogger } from '../utils/logger'

const logger = createLogger('StrategyProgressShareModal')

const StrategyProgressShareModal = ({ isOpen, onClose, strategyLevel, publicName }) => {
  const [copied, setCopied] = useState(false)
  const [generatingImage, setGeneratingImage] = useState(false)

  // Strategy display info
  const strategyInfo = {
    SAT_MATH: { name: 'SAT Math', icon: '🧮', color: '#3b82f6', gradient: ['#3b82f6', '#1d4ed8'] },
    SAT_MATH_MINI: {
      name: 'SAT Math Mini',
      icon: '⚡',
      color: '#f59e0b',
      gradient: ['#f59e0b', '#d97706']
    },
    SAT_READING_WRITING: {
      name: 'SAT R&W',
      icon: '📖',
      color: '#10b981',
      gradient: ['#10b981', '#059669']
    },
    SAT_READING_WRITING_MINI: {
      name: 'SAT R&W Mini',
      icon: '✏️',
      color: '#8b5cf6',
      gradient: ['#8b5cf6', '#7c3aed']
    }
  }

  const strategy = strategyInfo[strategyLevel?.strategyType] || strategyInfo.SAT_MATH
  const displayName = strategyLevelService.getStrategyDisplayName(strategyLevel?.strategyType)

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCopied(false)
    }
  }, [isOpen])

  // Generate profile link - uses /u/:publicName/:strategyType format
  const getProfileLink = () => {
    const baseUrl = window.location.origin
    if (!publicName) return baseUrl
    const strategyType = strategyLevel?.strategyType
    if (strategyType) {
      return `${baseUrl}/u/${encodeURIComponent(publicName)}/${strategyType}`
    }
    return `${baseUrl}/u/${encodeURIComponent(publicName)}`
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getProfileLink())
      setCopied(true)
      toast.success('Link copied to clipboard!')
      setTimeout(() => setCopied(false), 3000)
    } catch (error) {
      logger.error('Error copying link:', error)
      toast.error('Failed to copy link')
    }
  }

  /**
   * Generate share image as a File object
   * Returns a File that can be used with navigator.share() or downloaded
   */
  const generateShareImage = async () => {
    // Check for canvas support
    const canvas = document.createElement('canvas')
    if (!canvas.getContext) {
      throw new Error('Canvas not supported')
    }

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Could not get canvas context')
    }

    // Set canvas size (Instagram-friendly square 1080x1080)
    canvas.width = 1080
    canvas.height = 1080

    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height)
    gradient.addColorStop(0, strategy.gradient[0])
    gradient.addColorStop(1, strategy.gradient[1])
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Add subtle pattern overlay
    ctx.fillStyle = 'rgba(255, 255, 255, 0.05)'
    for (let i = 0; i < canvas.width; i += 40) {
      ctx.fillRect(i, 0, 1, canvas.height)
    }
    for (let i = 0; i < canvas.height; i += 40) {
      ctx.fillRect(0, i, canvas.width, 1)
    }

    // White card background
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)'
    roundRect(ctx, 90, 140, 900, 720, 40)
    ctx.fill()

    // Card shadow effect
    ctx.shadowColor = 'rgba(0, 0, 0, 0.1)'
    ctx.shadowBlur = 20
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 10

    // Reset shadow for text
    ctx.shadowColor = 'transparent'
    ctx.shadowBlur = 0
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 0

    // Strategy icon and name
    ctx.font = 'bold 48px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
    ctx.fillStyle = '#374151'
    ctx.textAlign = 'center'
    ctx.fillText(`${strategy.icon} ${displayName}`, 540, 230)

    // Level badge circle
    ctx.fillStyle = strategy.color
    ctx.beginPath()
    ctx.arc(540, 380, 100, 0, Math.PI * 2)
    ctx.fill()

    // Level number
    ctx.font = 'bold 72px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
    ctx.fillStyle = '#ffffff'
    ctx.fillText(`${strategyLevel.currentLevel || 1}`, 540, 400)

    // "Level" label
    ctx.font = '24px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
    ctx.fillText('LEVEL', 540, 440)

    // User name
    ctx.font = 'bold 42px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
    ctx.fillStyle = '#111827'
    ctx.fillText(publicName || 'Player', 540, 540)

    // Stats section
    const statsY = 630
    const statsWidth = 240
    const statsGap = 30

    // Avg Score
    drawStatBox(
      ctx,
      165,
      statsY,
      statsWidth,
      'Avg Score',
      `${strategyLevel.averageScore?.toFixed(0) || 0}%`,
      '#6b7280'
    )

    // Best Score
    drawStatBox(
      ctx,
      165 + statsWidth + statsGap,
      statsY,
      statsWidth,
      'Best Score',
      `${strategyLevel.bestScore?.toFixed(0) || 0}%`,
      '#10b981'
    )

    // Challenges
    drawStatBox(
      ctx,
      165 + (statsWidth + statsGap) * 2,
      statsY,
      statsWidth,
      'Challenges',
      `${strategyLevel.completedChallenges || 0}`,
      '#6b7280'
    )

    // Progress bar
    const progressY = 760
    const progressWidth = 700
    const progressHeight = 16
    const progressX = (canvas.width - progressWidth) / 2
    const progressPercent = Math.min((strategyLevel.currentLevel || 1) / 10, 1)

    // Progress bar background
    ctx.fillStyle = '#e5e7eb'
    roundRect(ctx, progressX, progressY, progressWidth, progressHeight, 8)
    ctx.fill()

    // Progress bar fill
    ctx.fillStyle = strategy.color
    roundRect(ctx, progressX, progressY, progressWidth * progressPercent, progressHeight, 8)
    ctx.fill()

    // Progress label
    ctx.font = '20px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
    ctx.fillStyle = '#6b7280'
    ctx.fillText(`Progress to Mastery: ${Math.round(progressPercent * 100)}%`, 540, 820)

    // Deaveris branding
    ctx.font = 'bold 32px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)'
    ctx.fillText('www.deaveris.com', 540, 960)

    // Profile link (subtle, smaller)
    const profilePath = strategyLevel?.strategyType
      ? `/u/${publicName}/${strategyLevel.strategyType}`
      : `/u/${publicName}`
    ctx.font = '18px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
    ctx.fillText(profilePath, 540, 1000)

    // Subtle tagline
    ctx.font = '18px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
    ctx.fillText('Master your SAT prep journey', 540, 1040)

    // Convert canvas to blob and then to File
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to generate image blob'))
            return
          }
          const fileName = `${displayName.replace(/\s+/g, '-').toLowerCase()}-progress-${publicName || 'share'}.png`
          const file = new File([blob], fileName, { type: 'image/png' })
          resolve(file)
        },
        'image/png',
        1.0
      )
    })
  }

  /**
   * Download a File object
   */
  const downloadFile = (file) => {
    const url = URL.createObjectURL(file)
    const link = document.createElement('a')
    link.download = file.name
    link.href = url

    if (typeof link.download !== 'undefined') {
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      window.open(url, '_blank')
    }

    URL.revokeObjectURL(url)
  }

  /**
   * Share to Instagram/social media using Web Share API on mobile
   * Falls back to download on desktop
   */
  const handleShareInstagram = async () => {
    if (!strategyLevel) return

    try {
      setGeneratingImage(true)

      // Generate the image as a File object
      const imageFile = await generateShareImage()

      // Check if Web Share API with files is supported (typically mobile only)
      if (navigator.canShare && navigator.canShare({ files: [imageFile] })) {
        await navigator.share({
          files: [imageFile]
          // Note: Don't include text/title when sharing files - it breaks on some platforms
        })
        toast.success('Shared successfully!')
      } else {
        // Fallback for desktop: download the image
        downloadFile(imageFile)
        toast.success('Image downloaded! Share it on Instagram Stories')
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // User cancelled the share - no error needed
        return
      }
      logger.error('Error sharing:', error)
      toast.error('Failed to share. Try downloading instead.')
    } finally {
      setGeneratingImage(false)
    }
  }

  const handleShareWhatsApp = () => {
    const text = `Check out my ${displayName} progress! ${strategy.icon}\n\nLevel ${strategyLevel?.currentLevel || 1} | Best Score: ${strategyLevel?.bestScore?.toFixed(0) || 0}%\n\nJoin me at www.deaveris.com`
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`
    window.open(url, '_blank')
  }

  const handleDownloadImage = async () => {
    if (!strategyLevel) return

    try {
      setGeneratingImage(true)
      const imageFile = await generateShareImage()
      downloadFile(imageFile)
      toast.success('Image downloaded!')
    } catch (error) {
      logger.error('Error generating image:', error)
      if (error.name === 'SecurityError') {
        toast.error('Browser security prevented image generation. Please try a different browser.')
      } else if (error.name === 'QuotaExceededError') {
        toast.error('Not enough memory to generate image. Please close other tabs and try again.')
      } else {
        toast.error('Failed to generate image. Please try again.')
      }
    } finally {
      setGeneratingImage(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Share Your Progress
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Preview Card */}
            <div
              className="rounded-xl p-6 text-white text-center"
              style={{
                background: `linear-gradient(135deg, ${strategy.gradient[0]}, ${strategy.gradient[1]})`
              }}
            >
              <div className="text-3xl mb-2">{strategy.icon}</div>
              <div className="text-sm opacity-80 mb-1">{displayName}</div>

              {/* Level Badge */}
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm mb-3">
                <div className="text-center">
                  <div className="text-3xl font-bold">{strategyLevel?.currentLevel || 1}</div>
                  <div className="text-xs opacity-80">LEVEL</div>
                </div>
              </div>

              <div className="text-lg font-bold mb-3">{publicName || 'Player'}</div>

              {/* Stats */}
              <div className="flex justify-center gap-4 text-sm">
                <div className="bg-white/20 rounded-lg px-3 py-2">
                  <div className="font-bold">{strategyLevel?.averageScore?.toFixed(0) || 0}%</div>
                  <div className="text-xs opacity-80">Avg</div>
                </div>
                <div className="bg-white/20 rounded-lg px-3 py-2">
                  <div className="font-bold">{strategyLevel?.bestScore?.toFixed(0) || 0}%</div>
                  <div className="text-xs opacity-80">Best</div>
                </div>
                <div className="bg-white/20 rounded-lg px-3 py-2">
                  <div className="font-bold">{strategyLevel?.completedChallenges || 0}</div>
                  <div className="text-xs opacity-80">Done</div>
                </div>
              </div>

              {/* Branding */}
              <div className="mt-4 pt-3 border-t border-white/20">
                <div className="text-sm font-medium">www.deaveris.com</div>
              </div>
            </div>

            {/* Share Link */}
            <div>
              <div className="flex items-center gap-2 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <input
                  type="text"
                  value={getProfileLink()}
                  readOnly
                  className="flex-1 bg-transparent text-sm text-gray-700 dark:text-gray-300 outline-none truncate"
                />
                <button
                  onClick={handleCopyLink}
                  className={`p-2 rounded-lg transition-colors ${
                    copied
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500'
                  }`}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={handleCopyLink}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <Link2 className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                <span className="text-xs text-gray-700 dark:text-gray-300">Copy Link</span>
              </button>

              <button
                onClick={handleShareInstagram}
                disabled={!strategyLevel}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 transition-colors disabled:opacity-50"
              >
                <Instagram className="w-6 h-6 text-white" />
                <span className="text-xs text-white">Instagram</span>
              </button>

              <button
                onClick={handleShareWhatsApp}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-green-500 hover:bg-green-600 transition-colors"
              >
                <MessageCircle className="w-6 h-6 text-white" />
                <span className="text-xs text-white">WhatsApp</span>
              </button>
            </div>

            {/* Download Button */}
            <button
              onClick={handleDownloadImage}
              disabled={!strategyLevel || generatingImage}
              className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors disabled:opacity-50"
            >
              {generatingImage ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span className="font-medium">Download Image</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Helper: Draw rounded rectangle
 */
const roundRect = (ctx, x, y, width, height, radius) => {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + width - radius, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
  ctx.lineTo(x + width, y + height - radius)
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
  ctx.lineTo(x + radius, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}

/**
 * Helper: Draw stat box on canvas
 */
const drawStatBox = (ctx, x, y, width, label, value, color) => {
  // Box background
  ctx.fillStyle = '#f3f4f6'
  roundRect(ctx, x, y, width, 80, 12)
  ctx.fill()

  // Value
  ctx.font = 'bold 32px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
  ctx.fillStyle = color
  ctx.textAlign = 'center'
  ctx.fillText(value, x + width / 2, y + 40)

  // Label
  ctx.font = '18px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
  ctx.fillStyle = '#6b7280'
  ctx.fillText(label, x + width / 2, y + 65)
}

export default StrategyProgressShareModal
